import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import ChatGPTMappingEditor from './ChatGPTMappingEditor';

const EMPTY_TEMPLATE = {
    name: '',
    description: '',
    gptUrl: '',
    sceneCount: 1,
    promptTemplate: 'Product Name: {productName}\nProduct Link: {productLink}\n\nKeluarkan output dalam format JSON yang diminta.',
    mapping: '',
};

const ChatGPTTabView = ({ selectedProductCount = 0, onRunChatGPT, isRunning = false, progress = { current: 0, total: 0, label: '' } }) => {
    const [systemTemplates, setSystemTemplates] = useState([]);
    const [userTemplates, setUserTemplates] = useState([]);
    const [activeTemplateId, setActiveTemplateId] = useState(null);
    const [showManage, setShowManage] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_TEMPLATE);
    const [showForm, setShowForm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    useEffect(() => {
        const ref = collection(db, 'system', 'chatgptTemplates', 'items');
        const unsub = onSnapshot(ref, (snap) => {
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setSystemTemplates(items);
        }, (err) => console.error('System templates load failed:', err));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!auth.currentUser) return;
        const ref = collection(db, 'users', auth.currentUser.uid, 'chatgptTemplates');
        const unsub = onSnapshot(ref, (snap) => {
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                // ⛔ Abaikan agent "Agent Saya" (PRO Studio) yang berkongsi collection
                //    ini — ia ditandai agentV:2 dan ada bar tersendiri di Workspace.
                //    Tanpa tapisan ini ia muncul di DUA tempat dan boleh dijalankan
                //    dengan enjin lama yang tidak faham resipinya.
                .filter(t => Number(t.agentV) !== 2);
            items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setUserTemplates(items);
        }, (err) => console.error('User templates load failed:', err));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!auth.currentUser) return;
        const ref = doc(db, 'users', auth.currentUser.uid);
        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) setActiveTemplateId(snap.data().chatgptActiveTemplateId || null);
        });
        return () => unsub();
    }, []);

    const setActiveTemplate = async (templateId) => {
        if (!auth.currentUser) return;
        try {
            await setDoc(doc(db, 'users', auth.currentUser.uid), { chatgptActiveTemplateId: templateId || null }, { merge: true });
        } catch (e) { alert('Gagal set active: ' + e.message); }
    };

    const openCreate = () => { setEditingId(null); setForm({ ...EMPTY_TEMPLATE }); setShowForm(true); };
    const openEdit = (tpl) => {
        setEditingId(tpl.id);
        setForm({
            name: tpl.name || '', description: tpl.description || '', gptUrl: tpl.gptUrl || '',
            sceneCount: tpl.sceneCount || 1,
            promptTemplate: tpl.promptTemplate || EMPTY_TEMPLATE.promptTemplate,
            mapping: typeof tpl.mapping === 'string' ? tpl.mapping : JSON.stringify(tpl.mapping || {}, null, 2),
        });
        setShowForm(true);
    };
    const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_TEMPLATE); };

    const handleSave = async () => {
        if (!auth.currentUser) return;
        if (!form.name.trim()) { alert('Name wajib diisi'); return; }
        if (!form.gptUrl.trim()) { alert('GPT URL wajib diisi'); return; }
        if (form.mapping.trim()) { try { JSON.parse(form.mapping); } catch (e) { alert('Mapping bukan JSON valid: ' + e.message); return; } }
        setIsSaving(true);
        try {
            const id = editingId || `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const ref = doc(db, 'users', auth.currentUser.uid, 'chatgptTemplates', id);
            const payload = {
                name: form.name.trim(), description: form.description.trim(), gptUrl: form.gptUrl.trim(),
                sceneCount: Number(form.sceneCount) === 2 ? 2 : 1,
                promptTemplate: form.promptTemplate, mapping: form.mapping.trim(),
                updatedAt: serverTimestamp(),
            };
            if (!editingId) payload.createdAt = serverTimestamp();
            await setDoc(ref, payload, { merge: true });
            closeForm();
        } catch (e) { alert('Save gagal: ' + e.message); } finally { setIsSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!auth.currentUser) return;
        try {
            await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'chatgptTemplates', id));
            if (activeTemplateId === id) await setActiveTemplate(null);
            setShowDeleteConfirm(null);
        } catch (e) { alert('Delete gagal: ' + e.message); }
    };

    const handleClone = async (sysTpl) => {
        if (!auth.currentUser) return;
        try {
            const existing = userTemplates.find(t => t.clonedFrom === sysTpl.id);
            if (existing && !confirm(`Kau dah ada clone "${existing.name}". Clone lagi sekali?`)) return;
            const id = `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            await setDoc(doc(db, 'users', auth.currentUser.uid, 'chatgptTemplates', id), {
                name: `${sysTpl.name} (clone)`, description: sysTpl.description || '', gptUrl: sysTpl.gptUrl || '',
                sceneCount: sysTpl.sceneCount || 1, promptTemplate: sysTpl.promptTemplate || '',
                mapping: typeof sysTpl.mapping === 'string' ? sysTpl.mapping : JSON.stringify(sysTpl.mapping || {}, null, 2),
                clonedFrom: sysTpl.id, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            });
        } catch (e) { alert('Clone gagal: ' + e.message); }
    };

    // Active template can be from My Templates OR System Templates (no clone needed to use)
    const activeTemplate =
        userTemplates.find(t => t.id === activeTemplateId) ||
        systemTemplates.find(t => t.id === activeTemplateId) ||
        null;
    const canRun = !!activeTemplate && selectedProductCount > 0 && !isRunning;

    return (
        <>
            {/* Compact 1-row toolbar */}
            <div className="px-3 sm:px-10 pt-4" style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
                <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 shadow-sm flex items-center gap-2 flex-wrap">
                    <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-base text-violet-500">smart_toy</span>
                    </div>
                    <span className="text-[11px] font-bold text-slate-700 hidden sm:inline">Agent AI</span>
                    <select
                        value={activeTemplateId || ''}
                        onChange={(e) => setActiveTemplate(e.target.value || null)}
                        className="flex-1 min-w-[140px] bg-slate-50 border border-slate-200 rounded-lg text-[11px] px-2 py-1.5 font-medium"
                    >
                        <option value="">— Pilih template —</option>
                        {userTemplates.length > 0 && (
                            <optgroup label="My Templates">
                                {userTemplates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({t.sceneCount === 2 ? '2sc' : '1sc'})</option>
                                ))}
                            </optgroup>
                        )}
                        {systemTemplates.length > 0 && (
                            <optgroup label="System Templates">
                                {systemTemplates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({t.sceneCount === 2 ? '2sc' : '1sc'})</option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                    <button
                        onClick={() => setShowManage(true)}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                        title="Manage Templates"
                    >
                        <span className="material-symbols-outlined text-[14px]">tune</span>
                        <span className="hidden sm:inline">Manage</span>
                    </button>
                    <button
                        onClick={() => onRunChatGPT && onRunChatGPT(activeTemplate)}
                        disabled={!canRun}
                        className={`px-3 py-1.5 rounded-lg text-white text-[11px] font-bold flex items-center gap-1 transition-all ${canRun
                            ? 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-sm'
                            : 'bg-slate-300 cursor-not-allowed'
                            }`}
                        title={!activeTemplate ? 'Pilih template dulu' : selectedProductCount === 0 ? 'Select produk dulu' : isRunning ? 'Sedang jalan...' : 'Run Agent AI'}
                    >
                        {isRunning ? (
                            <>
                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                {progress.current}/{progress.total}
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-[14px]">play_arrow</span>
                                Run ({selectedProductCount})
                            </>
                        )}
                    </button>
                </div>
                {isRunning && progress.label && (
                    <div className="mt-2 flex items-center gap-2 px-2 py-1.5 bg-violet-50 border border-violet-100 rounded-lg">
                        <div className="flex-1">
                            <div className="text-[10px] text-violet-700 font-bold mb-0.5 truncate">{progress.label}</div>
                            <div className="h-1 bg-violet-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-violet-500 to-purple-600 transition-all"
                                    style={{ width: progress.total > 0 ? `${Math.round((progress.current / progress.total) * 100)}%` : '0%' }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Manage Templates Modal */}
            {showManage && (
                <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowManage(false)}>
                    <div className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-lg text-violet-500">smart_toy</span>
                                <h3 className="text-sm font-bold text-slate-700">Agent AI Templates</h3>
                            </div>
                            <button onClick={() => setShowManage(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                                <span className="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>

                        <div className="flex-1 p-5 overflow-auto space-y-4">
                            {/* My Templates */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-base text-emerald-500">person</span>
                                        <h4 className="text-[12px] font-bold text-slate-700">My Templates</h4>
                                    </div>
                                    <button onClick={openCreate} className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[12px]">add</span>
                                        New
                                    </button>
                                </div>
                                {userTemplates.length === 0 ? (
                                    <p className="text-[11px] text-slate-400 text-center py-3">Belum ada. Klik "New" atau Clone dari System.</p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {userTemplates.map(tpl => (
                                            <div key={tpl.id} className={`flex items-center justify-between p-2.5 rounded-lg border ${activeTemplateId === tpl.id ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-100'}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <p className="text-[11px] font-bold text-slate-700 truncate">{tpl.name}</p>
                                                        <span className="text-[8px] px-1 py-0.5 bg-violet-100 text-violet-600 rounded font-bold">{tpl.sceneCount === 2 ? '2sc' : '1sc'}</span>
                                                        {tpl.clonedFrom && <span className="text-[8px] px-1 py-0.5 bg-amber-100 text-amber-600 rounded font-bold">Cloned</span>}
                                                        {activeTemplateId === tpl.id && <span className="text-[8px] px-1 py-0.5 bg-emerald-100 text-emerald-600 rounded font-bold">Active</span>}
                                                    </div>
                                                    <p className="text-[9px] text-slate-400 truncate font-mono">{tpl.gptUrl}</p>
                                                </div>
                                                <div className="flex items-center gap-0.5 ml-2">
                                                    <button onClick={() => openEdit(tpl)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-500" title="Edit">
                                                        <span className="material-symbols-outlined text-[14px]">edit</span>
                                                    </button>
                                                    <button onClick={() => setShowDeleteConfirm(tpl.id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-500" title="Delete">
                                                        <span className="material-symbols-outlined text-[14px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* System Templates */}
                            <div>
                                <div className="flex items-center gap-1.5 mb-2">
                                    <span className="material-symbols-outlined text-base text-blue-500">public</span>
                                    <h4 className="text-[12px] font-bold text-slate-700">System Templates</h4>
                                    <span className="text-[9px] text-slate-400">— preset admin</span>
                                </div>
                                {systemTemplates.length === 0 ? (
                                    <p className="text-[11px] text-slate-400 text-center py-3">Tiada system template lagi.</p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {systemTemplates.map(tpl => (
                                            <div key={tpl.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <p className="text-[11px] font-bold text-slate-700 truncate">{tpl.name}</p>
                                                        <span className="text-[8px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded font-bold">{tpl.sceneCount === 2 ? '2sc' : '1sc'}</span>
                                                    </div>
                                                    <p className="text-[9px] text-slate-400 truncate font-mono">{tpl.gptUrl}</p>
                                                </div>
                                                <button onClick={() => handleClone(tpl)} className="px-2 py-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-bold flex items-center gap-1 ml-2">
                                                    <span className="material-symbols-outlined text-[12px]">content_copy</span>
                                                    Clone
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Form Modal */}
            {showForm && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
                    <div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
                            <h3 className="text-sm font-bold text-slate-700">{editingId ? 'Edit Template' : 'New Template'}</h3>
                            <button onClick={closeForm} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                                <span className="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>
                        <div className="flex-1 p-5 overflow-auto space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Name *</label>
                                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: My Storyboard GPT" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-xs p-2.5" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Description</label>
                                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-xs p-2.5" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">GPT URL *</label>
                                <input type="text" value={form.gptUrl} onChange={(e) => setForm({ ...form, gptUrl: e.target.value })} placeholder="https://chatgpt.com/g/g-xxx-name" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-xs p-2.5 font-mono" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Scene Count</label>
                                <div className="flex gap-2">
                                    {[1, 2].map(n => (
                                        <button key={n} onClick={() => setForm({ ...form, sceneCount: n })} className={`flex-1 py-2 rounded-lg text-xs font-bold ${form.sceneCount === n ? 'bg-violet-500 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
                                            {n} Scene
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Prompt Template</label>
                                <p className="text-[9px] text-slate-400 mb-1.5">Placeholder: {`{productName}, {productLink}, {imageUrl}`}</p>
                                <textarea value={form.promptTemplate} onChange={(e) => setForm({ ...form, promptTemplate: e.target.value })} rows={5} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-xs p-2.5 font-mono resize-y" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Mapping (optional)</label>
                                <ChatGPTMappingEditor
                                    value={form.mapping}
                                    onChange={(val) => setForm({ ...form, mapping: val })}
                                    sceneCount={form.sceneCount}
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
                            <button onClick={closeForm} disabled={isSaving} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
                            <button onClick={handleSave} disabled={isSaving} className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-[11px] font-bold flex items-center gap-1 disabled:opacity-50">
                                {isSaving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h4 className="text-sm font-bold text-slate-800 mb-2">Delete template?</h4>
                        <p className="text-[11px] text-slate-500 mb-4">Template akan hilang. Tak boleh undo.</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowDeleteConfirm(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
                            <button onClick={() => handleDelete(showDeleteConfirm)} className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-bold">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatGPTTabView;
